import { EventEmitter, Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { StorageModule } from '../utils/storage.module';
import { STEP_ID } from '../enum/step-id.enum';
import { getRoutesByStep, IRoute, ROUTES } from '../utils/routes';
import { findKey } from 'lodash';
import { ErrorMessageEnum, ErrorTitleEnum, ErrorTypeEnum } from '../enum/error-title.enum';
import { Error, ErrorResponse } from '../model/error-response';
import { urlConfig } from '../../../config/url.config';
import { IErrorDialog } from '../components/page/page.component';
import * as Bowser from 'bowser';

const themeSettings = {
    active: 'light',
    light: {

    },
    dark: {

    }
}

const errorTokenTitles = [
    ErrorTitleEnum.STEP_TOKEN_EXPIRED,
];

const errorTokenMessages = [
    ErrorMessageEnum.UNAUTHORIZED,
    ErrorMessageEnum.JWT_EXPIRED,
    ErrorMessageEnum.JWT_INVALID,
    ErrorMessageEnum.INVALID_TOKEN_TYPE,
    ErrorMessageEnum.INVALID_TOKEN,
    ErrorMessageEnum.CREDENTIALS_MUST_BE_PROVIDED
];

const ALLOWED_ORIGINS = [
    urlConfig.landingCMCUrl,
    urlConfig.landingUrl
];

@Injectable({
    providedIn: 'root',
})
export class PageService {
    public showBackButton: boolean = false;
    public isPageLoading: boolean = false;
    public loadingTextTitle: string = 'Carregando';
    public loadingTextSubtitle: string = 'Por favor, aguarde ...';
    public isMobile: boolean = false;
    public themeChanged = new EventEmitter();
    public handleChangesModalError = new EventEmitter<IErrorDialog>();
    public showFaq = true;
    private _display: boolean = true;
    private _title: string;
    private _subtitle: string;
    private retryAttempt = 0;
    private retryLimit = 3;
    private formatedMessageModal: string;
    public deviceInfo = Bowser.parse(navigator.userAgent);


    private errorTokenTitles = [
        ErrorTitleEnum.STEP_TOKEN_EXPIRED,
    ];

    private errorTokenMessages = [
        ErrorMessageEnum.UNAUTHORIZED,
        ErrorMessageEnum.JWT_EXPIRED,
        ErrorMessageEnum.INVALID_TOKEN_TYPE
    ];

    constructor(
        @Inject(StorageModule) private storage: StorageModule,
        @Inject(Router) private router: Router,
    ) { }

    get display() {
        return this._display;
    }

    get title() {
        return this._title;
    }
    get subtitle() {
        return this._subtitle;
    }

    get themeColor() {
        return this.storage.getTheme().active;
    }

    hide() {
        this._display = false;
    }

    show() {
        this._display = true;
    }

    updateValues(values) {
        const { title, subtitle } = values;
        this._title = title;
        this._subtitle = subtitle;
    }

    setDarkTheme() {
        this.storage.setTheme({ ...themeSettings, active: 'dark' });
        this.themeChanged.emit(true);
    }

    setLightTheme() {
        this.storage.setTheme({ ...themeSettings, active: 'light' });
        this.themeChanged.emit(true);
    }

    goToLanding() {
        const urlRedirect = `${urlConfig.landingUrl}${this.storage.landingParameters}`;
        if(ALLOWED_ORIGINS.find(lp=>urlRedirect.indexOf(lp))){
            window.location.href = urlRedirect
        }
        else{
            window.location.href = urlConfig.landingUrl;
        }
    }

    goToPage(url: string, data?: any, reload: boolean = false) {
        if (data) {
            this.storeStepData(data)
        }
        if (reload)
            window.location.href = url;
        else
            this.router.navigate([url]);
    }

    goToNext(step: STEP_ID, data: any) {
        try {
            let routes: IRoute[] = getRoutesByStep(data.nextStep, data.flow);

            if (Object.keys(routes).length === 0) {
                this.goToPage(ROUTES.errorGeneric.url, data);
            }
            else {
                const objKey = findKey(routes[0], 'url');
                this.goToPage(routes[0][objKey]['url'], data);
            }
        } catch (error) {
            this.goToPage(ROUTES.errorGeneric.url, data);
        }
    }

    storeStepData(data) {
        this.storage.setCurrentStepData(data);
    }

    goToError(response: ErrorResponse,status?: number) {
        if(response.error) {
            this.storage.setDataErrorCode(response.error);

            const errorTokenExpired = errorTokenTitles.includes(response?.error?.title) || errorTokenMessages.includes(response.error.message);

            if(errorTokenExpired){
                this.retryAttempt = 0;
                this.formatedMessageModal = this.handleFormatError(response, ErrorMessageEnum.STEP_TOKEN_EXPIRED);
                this.handleCustomError({
                    statusCode: status,
                    title: ErrorTitleEnum.DEFAULT_WARNING,
                    description: this.formatedMessageModal,
                    isLoadError: response.error.isLoadRequest,
                    firstAction:{
                        name:'token',
                        title:'Logar novamente'
                    }
                });
            } else {
                if (this.retryAttempt < this.retryLimit) {

                    this.isPageLoading = response.error.isLoadRequest ? true : false;

                    this.handleCustomError({
                        statusCode: status,
                        title: ErrorTitleEnum.DEFAULT_WARNING,
                        description: this.handleFormatError(response),
                        isLoadError: response?.error.isLoadRequest,
                        firstAction: {
                            name: 'retry',
                            title: 'Tentar novamente'
                        },
                        serviceError: response.error
                    });
                    this.retryAttempt++
                } else {
                    this.isPageLoading = false;
                    this.retryAttempt = 0;
                    let beforeToken = false;
                    const currentRouter = window.location.pathname;

                    const routesBeforeToken = [
                        ROUTES.identification.url,
                        ROUTES.token_validation.url
                    ]

                    routesBeforeToken.forEach(url => {
                        if (currentRouter.includes(url)) {
                            beforeToken = true
                        }
                    })

                    if (beforeToken) {
                        this.goToPage(ROUTES.errorGeneric.url);
                    } else {
                        this.goToPage(ROUTES.identification.url);
                    }
                }
            }
        }
    }

    private handleFormatError(errorResponse: ErrorResponse, errorMessageDefault: ErrorMessageEnum = ErrorMessageEnum.DEFAULT_ERROR) {
        const message = (errorResponse?.error?.title) && ErrorMessageEnum[errorResponse?.error?.title]
            ? ErrorMessageEnum[errorResponse?.error?.title]
            : errorMessageDefault;

        return message;
    }

    private handleCustomError(error: IErrorDialog) {
        this.handleChangesModalError.emit(error);
    }

    isPageLoadingOn() {
        this.isPageLoading = true;
    }

    isPageLoadingOff() {
        this.isPageLoading = false;
    }
}
